import type { Map as MapboxMap } from "mapbox-gl";
import type { ElectoralDistrictsResponse, GeoJsonFeature, ElectoralDistrictFeatureProperties } from "../../api/map/types";
import { applyIncrementalUpdate, setCachedFeatures } from "./utils";

const SOURCE_ID = "electoral-districts";

/**
 * Ensures the electoral districts layer exists on the map (idempotent).
 * Returns a promise that resolves when the layer is ready (same pattern as postal codes).
 */
export const ensureElectoralDistrictsLayer = (map: MapboxMap): Promise<void> => {
    return new Promise((resolve) => {
        const createLayers = () => {
            try {
                if (!map.getSource(SOURCE_ID)) {
                    map.addSource(SOURCE_ID, {
                        type: "geojson",
                        data: { type: "FeatureCollection", features: [] },
                        promoteId: "electoralDistrictId"
                    });
                }

                // Alt 1: valdistrikt med lila fyllning (blandar snyggt med blått postnummer)
                if (!map.getLayer(`${SOURCE_ID}-fill`)) {
                    map.addLayer({
                        id: `${SOURCE_ID}-fill`,
                        type: "fill",
                        source: SOURCE_ID,
                        paint: {
                            "fill-color": [
                                "case",
                                ["boolean", ["feature-state", "selected"], false],
                                "#a78bfa",
                                "#ede9fe"
                            ],
                            "fill-opacity": [
                                "case",
                                ["boolean", ["feature-state", "selected"], false],
                                0.5,
                                0.35
                            ]
                        }
                    });
                }

                if (!map.getLayer(`${SOURCE_ID}-outline`)) {
                    map.addLayer({
                        id: `${SOURCE_ID}-outline`,
                        type: "line",
                        source: SOURCE_ID,
                        paint: {
                            "line-color": [
                                "case",
                                ["boolean", ["feature-state", "selected"], false],
                                "#7c3aed",
                                "#c4b5fd"
                            ],
                            "line-width": [
                                "interpolate",
                                ["linear"],
                                ["zoom"],
                                10,
                                1,
                                15,
                                1.5,
                                18,
                                2
                            ],
                            "line-opacity": 0.6
                        }
                    });
                }

                // Add labels for electoral district names (fallback to code if name missing)
                if (!map.getLayer(`${SOURCE_ID}-labels`)) {
                    map.addLayer({
                        id: `${SOURCE_ID}-labels`,
                        type: "symbol",
                        source: SOURCE_ID,
                        layout: {
                            "text-field": ["coalesce", ["get", "name"], ["get", "code"]],
                            "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                            "text-size": 11,
                            "text-anchor": "center",
                            "text-allow-overlap": false
                        },
                        paint: {
                            "text-color": "#6d28d9",
                            "text-halo-color": "#ffffff",
                            "text-halo-width": 1.5
                        }
                    });
                }

                resolve();
            } catch (error) {
                if (!map.isStyleLoaded()) {
                    map.once("style.load", () => createLayers());
                } else {
                    throw error;
                }
            }
        };

        if (!map.isStyleLoaded()) {
            const styleLoadHandler = () => {
                if (map.isStyleLoaded()) {
                    createLayers();
                } else {
                    setTimeout(() => {
                        if (map.isStyleLoaded()) {
                            createLayers();
                        } else {
                            resolve();
                        }
                    }, 100);
                }
            };
            map.once("style.load", styleLoadHandler);
            const checkInterval = setInterval(() => {
                if (map.isStyleLoaded()) {
                    clearInterval(checkInterval);
                    map.off("style.load", styleLoadHandler);
                    createLayers();
                }
            }, 100);
            setTimeout(() => clearInterval(checkInterval), 5000);
        } else {
            createLayers();
        }
    });
};

/**
 * Updates the electoral districts layer with data from the API
 * Uses incremental updates when possible to minimize re-rendering
 */
export const updateElectoralDistrictsLayer = (
    map: MapboxMap,
    data: ElectoralDistrictsResponse | null,
    useIncremental: boolean = true
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;

    if (!data || data.features.length === 0) {
        geojsonSource.setData({ type: "FeatureCollection", features: [] });
        return;
    }

    // Convert API response to Mapbox GeoJSON format
    const newFeatures: GeoJsonFeature<ElectoralDistrictFeatureProperties>[] = data.features.map((feature) => ({
        type: "Feature" as const,
        id: feature.id ?? feature.properties.electoralDistrictId,
        geometry: feature.geometry,
        properties: {
            ...feature.properties
        }
    }));

    // Use incremental update if enabled
    if (useIncremental) {
        try {
            applyIncrementalUpdate(
                map,
                geojsonSource,
                SOURCE_ID,
                newFeatures,
                (feature) => feature.id ?? feature.properties.electoralDistrictId
            );
            return;
        } catch (error) {
            // Fallback to full update on error
            console.warn("Incremental update failed, falling back to full update:", error);
        }
    }

    // Full update (incremental disabled or error)
    const geojson = {
        type: "FeatureCollection" as const,
        features: newFeatures
    };

    geojsonSource.setData(geojson as any);
    setCachedFeatures(map, SOURCE_ID, newFeatures);
};

/**
 * Removes the electoral districts layer from the map
 */
export const removeElectoralDistrictsLayer = (map: MapboxMap): void => {
    if (map.getLayer(`${SOURCE_ID}-labels`)) {
        map.removeLayer(`${SOURCE_ID}-labels`);
    }
    if (map.getLayer(`${SOURCE_ID}-outline`)) {
        map.removeLayer(`${SOURCE_ID}-outline`);
    }
    if (map.getLayer(`${SOURCE_ID}-fill`)) {
        map.removeLayer(`${SOURCE_ID}-fill`);
    }
    if (map.getSource(SOURCE_ID)) {
        map.removeSource(SOURCE_ID);
    }
};

/**
 * Applies selection highlight to electoral district features by setting feature-state.
 * Reorders features so selected are drawn last (higher z), so their outlines are visible on shared edges.
 */
export const setElectoralDistrictsSelectionHighlight = (
    map: MapboxMap,
    selectedIds: string[]
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") return;
    const geojsonSource = source as mapboxgl.GeoJSONSource & { _data?: { type: string; features?: Array<{ id?: string; properties?: { electoralDistrictId?: string } }> } };
    const data = geojsonSource._data;
    if (!data?.features) return;
    const idSet = new Set(selectedIds);
    for (const f of data.features) {
        const id = f.id ?? f.properties?.electoralDistrictId;
        if (id != null) {
            try {
                map.setFeatureState({ source: SOURCE_ID, id: String(id) }, { selected: idSet.has(String(id)) });
            } catch {
                // ignore
            }
        }
    }
    // Rita ej valda först, valda sist så att valda konturer hamnar ovanpå på delade kanter
    const sorted = {
        type: data.type,
        features: [...data.features].sort((a, b) => {
            const aId = a.id ?? a.properties?.electoralDistrictId;
            const bId = b.id ?? b.properties?.electoralDistrictId;
            const aSel = aId != null && idSet.has(String(aId));
            const bSel = bId != null && idSet.has(String(bId));
            if (aSel === bSel) return 0;
            return aSel ? 1 : -1;
        })
    };
    geojsonSource.setData(sorted as GeoJSON.FeatureCollection);
};

/**
 * Sets the visibility of the electoral districts layer
 */
export const setElectoralDistrictsLayerVisibility = (map: MapboxMap, visible: boolean): void => {
    const visibility = visible ? "visible" : "none";
    if (map.getLayer(`${SOURCE_ID}-fill`)) {
        map.setLayoutProperty(`${SOURCE_ID}-fill`, "visibility", visibility);
    }
    if (map.getLayer(`${SOURCE_ID}-outline`)) {
        map.setLayoutProperty(`${SOURCE_ID}-outline`, "visibility", visibility);
    }
    if (map.getLayer(`${SOURCE_ID}-labels`)) {
        map.setLayoutProperty(`${SOURCE_ID}-labels`, "visibility", visibility);
    }
};

const DEFAULT_FILL_COLOR_EXPRESSION = [
    "case",
    ["boolean", ["feature-state", "selected"], false],
    "#a78bfa",
    "#ede9fe"
] as unknown as mapboxgl.Expression;

/** Light red when metadata is shown but value is missing – so you see which districts need to be filled in */
const METADATA_NOVALUE_FILL = "#fecaca";

/** 9 muted green shades (low → high): near-white, then desaturated greens, ending in deep green – avoids brownish tones */
const METADATA_LEVEL_COLORS = [
    "#f5f9f4", "#e5efe2", "#cce0c7", "#a8c9a0", "#7bb06f",
    "#4d9645", "#2d7a2a", "#1a5f1a", "#0d450d"
] as const;

const METADATA_LEVELS = METADATA_LEVEL_COLORS.length;

/**
 * Switches electoral districts fill between default (purple) and metadata choropleth (green scale).
 * When minMax is set, fill uses 9 discrete levels: light green (low) to dark green (high).
 * When minMax is null, fill uses default purple and feature-state selected.
 */
export const setElectoralDistrictsMetadataPaint = (
    map: MapboxMap,
    minMax: { min: number; max: number } | null
): void => {
    const layerId = `${SOURCE_ID}-fill`;
    if (!map.getLayer(layerId)) return;

    if (minMax === null) {
        map.setPaintProperty(layerId, "fill-color", DEFAULT_FILL_COLOR_EXPRESSION);
        return;
    }

    const { min, max } = minMax;
    const range = max - min;
    if (range <= 0) {
        const expression: mapboxgl.Expression = [
            "case",
            ["has", "metadataValue"],
            METADATA_LEVEL_COLORS[0],
            METADATA_NOVALUE_FILL
        ];
        map.setPaintProperty(layerId, "fill-color", expression);
        return;
    }

    const stepSize = range / METADATA_LEVELS;
    const stops: number[] = [];
    for (let i = 1; i < METADATA_LEVELS; i++) {
        stops.push(min + stepSize * i);
    }

    const stepExpression: unknown[] = ["step", ["get", "metadataValue"], METADATA_LEVEL_COLORS[0]];
    for (let i = 0; i < stops.length; i++) {
        stepExpression.push(stops[i], METADATA_LEVEL_COLORS[i + 1]);
    }

    const expression: mapboxgl.Expression = [
        "case",
        ["has", "metadataValue"],
        stepExpression as mapboxgl.Expression,
        METADATA_NOVALUE_FILL
    ];
    map.setPaintProperty(layerId, "fill-color", expression);
};
